import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import NotificationBell from '../components/NotificationBell';
import {
  Video,
  Clock,
  Users,
  MessageSquare,
  Send,
  Play,
  Loader2,
  Calendar,
  Mic,
  Youtube,
  ExternalLink,
  Bell,
  BellRing,
  Share2,
  Heart,
  Copy,
  Check,
  Timer,
  Sparkles,
  ArrowLeft
} from 'lucide-react';

const WebinarWaitingRoom = () => {
  const { webinarId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, user } = useAuth();
  
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessInfo, setAccessInfo] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [attendeeCount, setAttendeeCount] = useState(0);
  const chatEndRef = useRef(null);

  useEffect(() => {
    fetchWebinarData();
  }, [webinarId]);

  useEffect(() => {
    if (!webinar?.date) return;

    const interval = setInterval(() => {
      const now = new Date();
      const webinarDate = new Date(webinar.date);
      const diff = webinarDate - now;

      if (diff <= 0) {
        setIsLive(true);
        setTimeLeft(null);
      } else {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setTimeLeft({ days, hours, minutes, seconds, total: diff });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [webinar]);

  useEffect(() => {
    // Simulate attendee count updates
    const interval = setInterval(() => {
      setAttendeeCount(prev => prev + Math.floor(Math.random() * 3));
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchWebinarData = async () => {
    try {
      const response = await api.get(`/webinars/${webinarId}`);
      setWebinar(response.data);
      setAttendeeCount(response.data.current_attendees || 0);

      if (isAuthenticated) {
        try {
          const accessResponse = await api.get(`/webinars/${webinarId}/access`);
          setAccessInfo(accessResponse.data);
        } catch (err) {
          // User not registered
        }
      }

      // Load simulated chat messages
      setChatMessages([
        { id: 1, user: 'UPIM', message: '¡Bienvenidos a la sala de espera! El webinar comenzará pronto.', isSystem: true, time: '10:00' },
        { id: 2, user: 'María G.', message: '¡Emocionada por este webinar!', time: '10:02' },
        { id: 3, user: 'Carlos R.', message: 'Primera vez que asisto, ¿cómo funciona?', time: '10:03' },
        { id: 4, user: 'UPIM', message: 'Cuando inicie el webinar, el video aparecerá automáticamente aquí.', isSystem: true, time: '10:04' },
      ]);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo cargar el webinar",
        variant: "destructive"
      });
      navigate('/webinars');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const newMsg = {
      id: Date.now(),
      user: user?.name || 'Invitado',
      message: newMessage,
      time: new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
    };

    setChatMessages(prev => [...prev, newMsg]);
    setNewMessage('');
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: "¡Enlace copiado!" });
    } catch {
      toast({ title: "No se pudo copiar", variant: "destructive" });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-900">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      </Layout>
    );
  }

  if (!webinar) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          <p>Webinar no encontrado</p>
        </div>
      </Layout>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-white hover:bg-white/10"
              onClick={() => navigate('/webinars')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
            <div className="hidden sm:block">
              <Badge className={isLive ? 'bg-red-500 text-white animate-pulse' : 'bg-white/10 text-white'}>
                {isLive ? (
                  <>
                    <span className="w-2 h-2 bg-white rounded-full mr-2 animate-ping" />
                    EN VIVO
                  </>
                ) : (
                  <>
                    <Timer className="h-3 w-3 mr-1" />
                    Sala de Espera
                  </>
                )}
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {webinar && (
              <NotificationBell 
                webinar={webinar} 
                webinarUrl={window.location.href}
                variant="ghost"
              />
            )}
            <Badge className="bg-white/10 text-white border-0">
              <Users className="h-3.5 w-3.5 mr-1.5" />
              {attendeeCount} en sala
            </Badge>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-white hover:bg-white/10"
              onClick={handleShare}
            >
              {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - Video/Countdown Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video/Countdown Card */}
            <Card className="bg-black/40 border-white/10 overflow-hidden">
              <div className="aspect-video relative flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                {isLive && accessInfo?.meeting_url ? (
                  // Show embedded video or link to live stream
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <Badge className="bg-red-500 text-white mb-4 animate-pulse">
                      <span className="w-2 h-2 bg-white rounded-full mr-2" />
                      EN VIVO AHORA
                    </Badge>
                    <Button 
                      size="lg" 
                      className="bg-red-600 hover:bg-red-700 h-14 px-8"
                      onClick={() => window.open(accessInfo.meeting_url, '_blank')}
                    >
                      <Play className="h-5 w-5 mr-2" />
                      Ver Transmisión
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                ) : (
                  // Show countdown
                  <div className="text-center px-4">
                    <Sparkles className="h-12 w-12 text-white/50 mx-auto mb-4" />
                    <h2 className="text-white text-xl sm:text-2xl font-bold mb-6">
                      El webinar comenzará en:
                    </h2>
                    
                    {timeLeft && (
                      <div className="flex justify-center gap-3 sm:gap-4 mb-6">
                        {timeLeft.days > 0 && (
                          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 min-w-[70px] sm:min-w-[80px]">
                            <p className="text-3xl sm:text-4xl font-bold text-white">{timeLeft.days}</p>
                            <p className="text-xs text-gray-400 uppercase">Días</p>
                          </div>
                        )}
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 min-w-[70px] sm:min-w-[80px]">
                          <p className="text-3xl sm:text-4xl font-bold text-white">{String(timeLeft.hours).padStart(2, '0')}</p>
                          <p className="text-xs text-gray-400 uppercase">Horas</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 min-w-[70px] sm:min-w-[80px]">
                          <p className="text-3xl sm:text-4xl font-bold text-white">{String(timeLeft.minutes).padStart(2, '0')}</p>
                          <p className="text-xs text-gray-400 uppercase">Min</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 min-w-[70px] sm:min-w-[80px]">
                          <p className="text-3xl sm:text-4xl font-bold text-white">{String(timeLeft.seconds).padStart(2, '0')}</p>
                          <p className="text-xs text-gray-400 uppercase">Seg</p>
                        </div>
                      </div>
                    )}

                    {!accessInfo && isAuthenticated && (
                      <Button 
                        className="bg-white text-gray-900 hover:bg-gray-100"
                        onClick={async () => {
                          try {
                            await api.post(`/webinars/${webinarId}/register`);
                            toast({ title: "¡Te has registrado!" });
                            fetchWebinarData();
                          } catch (err) {
                            toast({ title: "Error", description: err.response?.data?.detail, variant: "destructive" });
                          }
                        }}
                      >
                        <Bell className="h-4 w-4 mr-2" />
                        Registrarme para notificaciones
                      </Button>
                    )}

                    {!isAuthenticated && (
                      <Button 
                        className="bg-white text-gray-900 hover:bg-gray-100"
                        onClick={() => navigate('/login')}
                      >
                        Inicia sesión para registrarte
                      </Button>
                    )}
                  </div>
                )}

                {/* Decorative elements */}
                <div className="absolute top-4 left-4">
                  <Badge className="bg-white/10 text-white border-0">
                    <Youtube className="h-3.5 w-3.5 mr-1.5 text-red-500" />
                    YouTube Live
                  </Badge>
                </div>
              </div>
            </Card>

            {/* Webinar Info */}
            <Card className="bg-black/40 border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <Badge className="mb-2 bg-white/10 text-white border-0">
                      {webinar.category}
                    </Badge>
                    <h1 className="text-2xl font-bold text-white mb-2">{webinar.title}</h1>
                    <div className="flex items-center text-gray-400 text-sm">
                      <Mic className="h-4 w-4 mr-1.5" />
                      {webinar.instructor}
                    </div>
                  </div>
                  <Badge className={
                    webinar.level === 'basico' ? 'bg-green-500/20 text-green-400' :
                    webinar.level === 'intermedio' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }>
                    {webinar.level}
                  </Badge>
                </div>

                <p className="text-gray-300 mb-6">{webinar.description}</p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <Calendar className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-white font-medium">{formatDate(webinar.date)}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <Clock className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-white font-medium">{formatTime(webinar.date)}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <Timer className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-white font-medium">{webinar.duration_minutes} min</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <Users className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-white font-medium">Sin límite</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chat Sidebar */}
          <div className="lg:col-span-1">
            <Card className="bg-black/40 border-white/10 h-[600px] flex flex-col">
              <div className="p-4 border-b border-white/10">
                <h3 className="font-semibold text-white flex items-center">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Chat de la Sala
                </h3>
                <p className="text-xs text-gray-400 mt-1">Interactúa con otros asistentes</p>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMessages.map(msg => (
                  <div 
                    key={msg.id} 
                    className={`${msg.isSystem ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-white/5'} rounded-lg p-3`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium ${msg.isSystem ? 'text-blue-400' : 'text-white'}`}>
                        {msg.user}
                      </span>
                      <span className="text-xs text-gray-500">{msg.time}</span>
                    </div>
                    <p className="text-sm text-gray-300">{msg.message}</p>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-white/10">
                {isAuthenticated ? (
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Escribe un mensaje..."
                      className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                    <Button type="submit" size="icon" className="bg-white/10 hover:bg-white/20">
                      <Send className="h-4 w-4 text-white" />
                    </Button>
                  </form>
                ) : (
                  <Button 
                    className="w-full bg-white/10 text-white hover:bg-white/20"
                    onClick={() => navigate('/login')}
                  >
                    Inicia sesión para chatear
                  </Button>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebinarWaitingRoom;
